/*
==============================================================================
NUPEP Cart Framework
Version: 7.0.0

Central Cart Controller

Author:
NUPEP Framework

Responsibilities
----------------
✓ Own cart state
✓ Own loading state
✓ All cart mutations
✓ Central refresh pipeline
✓ Single source of truth
==============================================================================
*/

"use strict";

window.NUPEP = window.NUPEP || {};

NUPEP.Cart = {
  /*
    --------------------------------------------------------------------------
    State
    --------------------------------------------------------------------------
    */

  state: null,

  loading: false,

  initialized: false,

  /*
    --------------------------------------------------------------------------
    Initialise
    --------------------------------------------------------------------------
    */

  async init() {
    if (this.initialized) return;

    console.log("NUPEP Cart Framework v7.0");

    this.initialized = true;

    await this.refresh();
  },

  /*
    --------------------------------------------------------------------------
    Refresh Entire Cart
    --------------------------------------------------------------------------
    */

  async refresh() {
    if (this.loading) return this.state;

    this.loading = true;

    try {
      this.state = await NUPEP.CartAPI.getCart();

      await NUPEP.CartRenderer.render(this.state);

      if (NUPEP.CartEvents) {
        NUPEP.CartEvents.bind();
      }

      return this.state;
    } finally {
      this.loading = false;
    }
  },

  /*
    --------------------------------------------------------------------------
    Get Current State
    --------------------------------------------------------------------------
    */

  getState() {
    return this.state;
  },

  /*
    --------------------------------------------------------------------------
    Add Product
    --------------------------------------------------------------------------
    */

  async add(variantId, quantity = 1, sellingPlan = null) {
    await NUPEP.CartAPI.add(variantId, quantity, sellingPlan);

    return await this.refresh();
  },

  /*
    --------------------------------------------------------------------------
    Change Quantity
    --------------------------------------------------------------------------
    */

  async change(lineKey, quantity) {
    await NUPEP.CartAPI.change(lineKey, quantity);

    return await this.refresh();
  },

  /*
    --------------------------------------------------------------------------
    Remove Line
    --------------------------------------------------------------------------
    */

  async remove(lineKey) {
    await NUPEP.CartAPI.remove(lineKey);

    return await this.refresh();
  },

  /*
--------------------------------------------------------------------------
Replace Subscription
--------------------------------------------------------------------------
*/

  async replaceSubscription(button) {
    return await NUPEP.CartSubscriptions.replace(button);
  },

  /*
    --------------------------------------------------------------------------
    Clear Cart
    --------------------------------------------------------------------------
    */

  async clear() {
    if (!this.state) {
      await this.refresh();
    }

    const items = [...this.state.items];

    for (const item of items) {
      await NUPEP.CartAPI.remove(item.key);
    }

    return await this.refresh();
  },

  /*
    --------------------------------------------------------------------------
    Find Line
    --------------------------------------------------------------------------
    */

  findLine(key) {
    if (!this.state) return null;

    return this.state.items.find((item) => item.key === key);
  },

  /*
    --------------------------------------------------------------------------
    Find Variant
    --------------------------------------------------------------------------
    */

  findVariant(variantId) {
    if (!this.state) return [];

    return this.state.items.filter(
      (item) => Number(item.variant_id) === Number(variantId),
    );
  },

  /*
    --------------------------------------------------------------------------
    Cart Count
    --------------------------------------------------------------------------
    */

  count() {
    if (!this.state) return 0;

    return this.state.item_count;
  },

  /*
    --------------------------------------------------------------------------
    Total Price
    --------------------------------------------------------------------------
    */

  total() {
    if (!this.state) return 0;

    return this.state.total_price;
  },

  /*
    --------------------------------------------------------------------------
    Has Subscription
    --------------------------------------------------------------------------
    */

  hasSubscriptions() {
    if (!this.state) return false;

    return this.state.items.some((item) => item.selling_plan_allocation);
  },
};

/*
==============================================================================
Boot Framework
==============================================================================
*/

document.addEventListener(
  "DOMContentLoaded",

  () => {
    NUPEP.Cart.init();
  },
);
